import { useEffect, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLiveStreamSetup } from '@/hooks/useLiveStreamSetup'
import { usePermissions } from '@/hooks/usePermissions'
import Link from 'next/link'
import { BiTrendingDown } from 'react-icons/bi'
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi'
import { MdFormatListBulleted } from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'

import { Badge, IconButtonProps } from '@chakra-ui/react'
import { Box, Flex, Grid, Icon, IconButton } from '@chakra-ui/react'
import styled from '@emotion/styled'

import {
  AiOperator,
  CaseManagement,
  Dashboard,
  EmergencyCall,
  Facility,
  Gateway,
  Globe,
  GuardTour,
  Incident,
  Rule,
  SecurityDevice,
} from '@/components/icons'
import { selectSideNavOpen, toggleSideNavOpen } from '@/redux/ui/uiSlice'
import { SIDE_BAR_WIDTH_CLOSED, SIDE_BAR_WIDTH_OPEN } from '@/styles/theme'
import { BUTTON_PRESS, mixpanel, trackEvent } from '@/utils/analytics'
import {
  AI_OPERATOR_HUB,
  CASE_MANAGEMENT,
  DASHBOARD,
  DEVICES,
  EMERGENCY_CALL_LOG,
  FACILITIES,
  GATEWAYS,
  GLOBAL,
  GUARD_TOURS,
  INCIDENTS,
  INCIDENT_RULES,
  METRICS_DASHBOARD,
  STANDARD_OPERATING_PROCEDURES,
} from '@/utils/routes'

import { SideBarEditMode } from './SideBarEditMode'
import { SideBarNavItem } from './SideBarNavItem'
import { SideBarProfile } from './SideBarProfile'

const SideBarStyles = styled.div<{ $show: boolean }>`
  top: 0;
  left: 0;
  width: ${(p) => (p.$show ? SIDE_BAR_WIDTH_OPEN : SIDE_BAR_WIDTH_CLOSED)};
  background: linear-gradient(180deg, #16357f 0%, #091848 100%);
  box-shadow: 4px 0 14px 0 rgba(65, 71, 95, 0.17);
  transition: ease-in-out 0.3s width;
  z-index: 100;
  min-height: 0;
`

const NavToggle = styled(IconButton as React.ComponentType<IconButtonProps>)`
  position: absolute;
  right: 0;
  top: 30px;
  transform: translate(50%, -50%);
  background-color: #fff;
  font-size: 20px;
  height: 18px;
  min-width: 18px;
`

const NewBadge = () => {
  return (
    <Badge
      bg='signalyellow-400'
      borderRadius='full'
      color='black'
      fontSize='10px'
      px='6px'
      py='1px'
    >
      NEW
    </Badge>
  )
}

export const SideBar = () => {
  const dispatch = useDispatch()
  const isSideNavOpen = useSelector(selectSideNavOpen)
  const {
    shouldShowDashboardPage,
    shouldShowEditMode,
    shouldShowSOPPage,
    shouldShowIncidentRulesPage,
    shouldShowCaseManagementPage,
    shouldShowOperatorHubPage,
    shouldShowOperatorHub,
  } = usePermissions()

  const { hasNoLiveStream, isOrangePeel } = useLiveStreamSetup()

  const aioHubKey = 'ai_operator_hub_visited'
  const [shouldShowOperatorHubBadge, setShouldShowOperatorHubBadge] =
    useState(false)

  // Used to show the New badge for OperatorHub on first time use
  useEffect(() => {
    if (shouldShowOperatorHub && typeof window !== 'undefined') {
      const visited = localStorage.getItem(aioHubKey)
      if (visited) {
        setShouldShowOperatorHubBadge(false)
      }
    }
  }, [shouldShowOperatorHub])

  const onOperatorHubClick = () => {
    trackEvent(`${BUTTON_PRESS} AI Operator Hub, Sidebar`, {
      showingNewBadge: shouldShowOperatorHubBadge,
    })
    if (shouldShowOperatorHubBadge) {
      localStorage.setItem(aioHubKey, 'true')
      setShouldShowOperatorHubBadge(false)
    }
  }

  const shouldShowLegacyDashboard = useFeatureFlag(FeatureFlag.legacyDashboard)
  const isEmergencyCallEnabled = useFeatureFlag(FeatureFlag.emergencyCall)
  const isGuardToursEnabled = useFeatureFlag(FeatureFlag.guardTours)
  const isCaseManagementEnabled = useFeatureFlag(FeatureFlag.caseManagement)
  const isIncidentRulesEnabled = useFeatureFlag(FeatureFlag.incidentRules)
  const isIPv6RedirectionEnabled = useFeatureFlag(FeatureFlag.ipv6Redirection)

  return (
    <SideBarStyles $show={isSideNavOpen}>
      <Flex
        as='nav'
        direction='column'
        h='100%'
        justifyContent='space-between'
        position='relative'
      >
        <NavToggle
          aria-label='Toggle Sidebar'
          borderRadius='full'
          boxShadow='0px 0px 4px rgba(0, 0, 0, 0.5)'
          data-testid='sidebar_expand_toggle'
          icon={
            <Icon
              as={isSideNavOpen ? HiOutlineChevronLeft : HiOutlineChevronRight}
              boxSize='16px'
              color='#4C535F'
            />
          }
          onClick={() => dispatch(toggleSideNavOpen())}
          size='xs'
        />
        <Grid h='full' templateRows='60px auto'>
          <Box>
            <Flex
              alignItems='center'
              as={Link}
              cursor='pointer'
              h='60px'
              href={GLOBAL}
              overflow='hidden'
              px='13px'
            >
              {isSideNavOpen ? (
                <img
                  alt='HiveWatch'
                  height={34}
                  src='/FullLogo-White.svg'
                  width={149}
                />
              ) : (
                <img
                  alt='HiveWatch'
                  src='/Icon-White.svg'
                  style={{ height: 34, width: 'auto' }}
                />
              )}
            </Flex>
          </Box>
          <Flex
            css={{
              '&::-webkit-scrollbar': {
                display: 'none',
                width: 0,
              },
            }}
            flexDirection='column'
            justifyContent='space-between'
            overflowY='auto'
            pt='20px'
          >
            <Box>
              {!isIPv6RedirectionEnabled && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_global'
                  icon={Globe}
                  label='Global'
                  path={GLOBAL}
                />
              )}
              <SideBarNavItem
                dataTestId='sidebar_navItem_facilities'
                icon={Facility}
                label='Facilities'
                path={FACILITIES}
              />
              <SideBarNavItem
                dataTestId='sidebar_navItem_incidents'
                icon={Incident}
                label='Incidents'
                path={INCIDENTS}
              />
              {!hasNoLiveStream && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_devices'
                  icon={SecurityDevice}
                  label='Devices'
                  path={DEVICES}
                />
              )}

              {shouldShowOperatorHubPage && (
                <SideBarNavItem
                  badge={shouldShowOperatorHubBadge ? <NewBadge /> : undefined}
                  dataTestId='sidebar_navItem_aiOperatorHub'
                  icon={AiOperator}
                  label='AI Operator Hub'
                  onClick={onOperatorHubClick}
                  path={AI_OPERATOR_HUB}
                />
              )}

              {shouldShowDashboardPage &&
                (shouldShowLegacyDashboard ? (
                  <SideBarNavItem
                    dataTestId='sidebar_navItem_metrics'
                    icon={Dashboard}
                    label='Metrics'
                    path={METRICS_DASHBOARD}
                  />
                ) : (
                  <SideBarNavItem
                    dataTestId='sidebar_navItem_dashboard'
                    icon={BiTrendingDown}
                    label='Dashboard'
                    path={DASHBOARD}
                  />
                ))}
              {shouldShowSOPPage && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_sop'
                  icon={MdFormatListBulleted}
                  label='SOPs'
                  path={STANDARD_OPERATING_PROCEDURES}
                />
              )}
              {isEmergencyCallEnabled && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_emergencyCalling'
                  icon={EmergencyCall}
                  label='Emergency Calls'
                  path={EMERGENCY_CALL_LOG}
                />
              )}
              {isGuardToursEnabled && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_guardTours'
                  icon={GuardTour}
                  label='Guard Tours'
                  onClick={() =>
                    mixpanel.track('Button Press: Nav - Guard Tours List')
                  }
                  path={GUARD_TOURS}
                />
              )}
              {isOrangePeel && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_gateways'
                  icon={Gateway}
                  label='Gateways'
                  path={GATEWAYS}
                />
              )}
              {isCaseManagementEnabled && shouldShowCaseManagementPage && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_case_management'
                  icon={CaseManagement}
                  label='Case Management'
                  path={CASE_MANAGEMENT}
                />
              )}

              {isIncidentRulesEnabled && shouldShowIncidentRulesPage && (
                <SideBarNavItem
                  dataTestId='sidebar_navItem_incidentRules'
                  icon={Rule}
                  iconSize='32px'
                  label='Rules'
                  path={INCIDENT_RULES}
                />
              )}
            </Box>
            <Box>
              {shouldShowEditMode && <SideBarEditMode />}
              <SideBarProfile />
            </Box>
          </Flex>
        </Grid>
      </Flex>
    </SideBarStyles>
  )
}
